import { useState, useCallback, useEffect, type JSX } from 'react'

const MIN_COLUMN_WIDTH = 60

/**
 * Self-contained resize handle — same pattern as ConversationTable.
 * Handles its own mousemove/mouseup listeners so dragging works even
 * if the cursor leaves the thin handle strip mid-drag.
 */
export function ResizeHandle({
  startWidth,
  onResize,
  onResizeEnd,
}: {
  startWidth: number
  onResize: (width: number) => void
  onResizeEnd: (width: number) => void
}): JSX.Element {
  const [isResizing, setIsResizing] = useState(false)

  const handleMouseDown = (e: React.MouseEvent<HTMLDivElement>): void => {
    e.preventDefault()
    e.stopPropagation()
    const startX = e.clientX
    let currentWidth = startWidth
    setIsResizing(true)

    const handleMouseMove = (moveEvent: MouseEvent): void => {
      currentWidth = Math.max(MIN_COLUMN_WIDTH, Math.round(startWidth + (moveEvent.clientX - startX)))
      onResize(currentWidth)
    }
    const handleMouseUp = (): void => {
      document.removeEventListener('mousemove', handleMouseMove)
      document.removeEventListener('mouseup', handleMouseUp)
      setIsResizing(false)
      onResizeEnd(currentWidth)
    }
    document.addEventListener('mousemove', handleMouseMove)
    document.addEventListener('mouseup', handleMouseUp)
  }

  return (
    <div
      onMouseDown={handleMouseDown}
      onClick={(e) => e.stopPropagation()}
      className="absolute right-0 top-0 z-40 flex h-full w-2.5 cursor-col-resize select-none items-center justify-end"
      title="Drag to resize column"
    >
      <div className={`h-1/2 transition-colors ${isResizing ? 'w-[2px] bg-[#2952CC]' : 'w-px bg-gray-300 hover:w-[2px] hover:bg-[#3366FF]'}`} />
    </div>
  )
}

export function useColumnResize(initialWidths: number[]) {
  const [columnWidths, setColumnWidths] = useState<number[]>(initialWidths)

  useEffect(() => {
    if (initialWidths.length !== columnWidths.length) {
      setColumnWidths(initialWidths)
    }
  }, [initialWidths, columnWidths.length])

  const handleResize = useCallback((index: number, width: number): void => {
    setColumnWidths((prev) => {
      const next = [...prev]
      next[index] = width
      return next
    })
  }, [])

  const resetWidths = useCallback(() => {
    setColumnWidths(initialWidths)
  }, [initialWidths])

  return {
    columnWidths,
    handleResize,
    resetWidths,
  }
}
